import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { StudiesService } from '../studies.service';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { SitesService } from 'src/app/sites/sites.service';
import { EnterpriseService } from 'src/app/enterprise/enterprise.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { DeviceCommonService } from '../../home/site-dashboard/device/device-common.service';
import { ValidateFormDirective } from 'src/app/shared/directives/validate-form';
import { DeviceService } from '../../home/site-dashboard/device/device.service';
import * as moment from 'moment';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-new-study',
	templateUrl: './new-study.component.html',
	styleUrls: ['./new-study.component.css']
})
export class NewStudyComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	@Input() studies = [];
	@Input() parent: 'device'|'admin'|'customer'|'site'|'study' = 'admin';
	@Input() customerId = 0;
	@Input() siteId = 0;
	@Input() deviceId = '';
	@Input() studyInfo;
	@Input() device;
	@Input() editMode = false;
	@Input() editStudyData: any = null;

	@Output() onClose = new EventEmitter<number>();

	currentUser = this.usersService.getCurrentUser();

	customers: any[] = [];
	sites: any[] = [];
	devices: any[] = [];
	devicesQueuedStudies: any = {};
	devicesOngoingStudies: any = {};
	enterprisesList: any[] = [];

	// Wizard state
	currentStep = 1;
	totalSteps = 5;
	readonly showShareWithCustomerStep = false;
	successMessage = '';
	formError = null;
	apiFieldErrors: Record<string, string> = {};

	// Step 1: Device Setup
	deviceSetupData = {
		truckId: '',
		truckSn: '',
		truckManufacturer: '',
		truckManufacturingYear: null,
		truckType: '',
		numberOfCables: null,
		batteryCapacity: null,
		batteryType: '',
		nominalBatteryVoltage: null,
	};

	readonly batteryTypeOptions = [
		{ id: 0, label: this.translateService.instant('events.battery_type_0') },
		{ id: 2, label: this.translateService.instant('events.battery_type_2') }
	];

	readonly batteryVoltageOptionsByType: Record<number, number[]> = {
		[this.deviceService.batteryTypesByName.lead]: [24, 36, 48, 72, 80],
		[this.deviceService.batteryTypesByName.lithuim_ion]: [36, 54, 72, 108, 120]
	};

	// Step 2: Study Details
	isNewCustomer = false;
	isNewSite = false;

	studyDetailsData = {
		studyName: '',
		customerId: '',
		siteId: '',
		serialNumber: ''
	};

	// Step 3: New Customer
	newCustomer: any = {
		id: 0,
		customer_name: '',
		customer_contact_name: '',
		customer_phone_num: '',
		customer_enterprises: null
	};

	// Step 4: New Site
	newSite: any = {
		id: 0,
		name: '',
		zone_id: null,
		installation_date: new Date(new Date().setHours(0, 0, 0, 0)),
		is_smart_rebates: false,
		password_mode: 'no_touch',
		is_verified: true,
		is_contain_iotemeter_devices: false,
		customer_enterprises: [],
	};
	newSiteMap: any = {
		latitude: 28.623421,
		longitude: -81.212219
	};

	customerEnterprises: any[] = [];

	// Step 5: Share
	shareWithCustomer: boolean = false;

	resultCode = {
		not_found: 2,
		study_already_exists: 3,
		not_iotah_device: 4,
		site_not_found: 5,
		has_queued_study: 6,
		incorrect_site: 7,
		customer_name_exists: 8,
		site_name_exists: 9,
	};

	isTruckManufacturingYearValid = true;

	@ViewChild('newStudyForm') newStudyForm: ModalComponent;
	@ViewChild('deviceSetupValidate') deviceSetupValidate?: ValidateFormDirective;
	@ViewChild('studyDetailsValidate') studyDetailsValidate?: ValidateFormDirective;
	@ViewChild('newCustomerValidate') newCustomerValidate?: ValidateFormDirective;
	@ViewChild('newSiteValidate') newSiteValidate?: ValidateFormDirective;

	constructor(
		private studiesService: StudiesService,
		private translateService: TranslateService,
		public usersService: UsersService,
		public sitesService: SitesService,
		private enterpriseService: EnterpriseService,
		public deviceCommonService: DeviceCommonService,
		private deviceService: DeviceService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		if (this.editMode) {
			this.initEditMode();
			return;
		}

		this.getData();
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.openNewStudyForm();
	}

	ngAfterViewInit(): void {
		this.newStudyForm.show();
		this.newStudyForm.onClose.subscribe((ok) => {
			if (!ok) {
				this.onClose.emit(0);
				this.newStudyForm.hide();
				this.resetAll();
			}
		});
	}

	openNewStudyForm() {
		if (['customer', 'site'].includes(this.parent)) {
			this.studyDetailsData.customerId = +this.customerId as any;
			this.getCustomerSites(+this.customerId);
		}

		if (['site'].includes(this.parent))
			this.studyDetailsData.siteId = +this.siteId as any;

		if (['study', 'device'].includes(this.parent))
			this.studyDetailsData.serialNumber = this.device?.mac_address;

		if (this.newStudyForm)
			this.newStudyForm.show();
	}

	getData() {
		this.sitesService.getUserSitesInfo({get_customers: true}).subscribe(
			(data: any) => {
				this.customers = data.customers.sort((item1, item2) => item1.customer_name.toLowerCase() > item2.customer_name.toLowerCase() ? 1 : -1);

				this.studiesService.getAllStudyDevices().subscribe((res: any) => {
					this.devices = res.devices;
					this.devicesQueuedStudies = res.queuedStudies;
					this.devicesOngoingStudies = res.ongoingStudies;
					this.checkOngoingStudies();
					if (+this.customerId)
						this.getCustomerSites(+this.customerId);
				});
			}
		);


		if (this.currentUser?.isSCT) {
			this.enterpriseService.getEnterprises({ source: 'studies', customer_edit: true }).subscribe((res: any) => {
				this.enterprisesList = res;
			});
		} else if (this.currentUser?.enterprise_id) {
			this.newCustomer.customer_enterprises = [Number(this.currentUser.enterprise_id)];
			this.newSite.enterprise_ids = [Number(this.currentUser.enterprise_id)];
		}
	}

	checkOngoingStudies() {
		[...this.devices].forEach(device => {
			device.label = device.serial_number;
			const ongoingStudyName = this.devicesOngoingStudies[device.mac_address];
			const queuedStudyName = this.devicesQueuedStudies[device.mac_address];

			if (ongoingStudyName)
				device.label += ` (${this.translateService.instant('studies.status_not_done')}: ${ongoingStudyName})`;
			else if (queuedStudyName)
				device.label += ` (${this.translateService.instant('studies.status_queued')}: ${queuedStudyName})`;
		});
	}

	getCustomerSites(customerId) {
		this.sites = [];
		if (customerId) {
			for (let customer of this.customers) {
				if (customer.id == customerId) {
					this.sites = customer.sites.filter(site => site.name !== this.commonService.powerStudiesDevicesSiteName).sort((item1, item2) => item1.name.toLowerCase() > item2.name.toLowerCase() ? 1 : -1);
					break;
				}
			}
		}
	}

	onCustomerChange(customerId) {
		this.studyDetailsData.siteId = '';
		this.getCustomerSites(customerId);
	}

	onNewCustomerChange() {
		if (this.isNewCustomer) {
			this.isNewSite = true;
			this.studyDetailsData.customerId = '';
			this.studyDetailsData.siteId = '';
		}
	}

	onNewSiteChange() {
		if (this.isNewSite) {
			this.studyDetailsData.siteId = '';
		} else if (this.isNewCustomer) {
			// If new customer is checked, new site must be checked
			this.isNewSite = true;
		}
	}

	// ====== Wizard Navigation ======

	nextStep() {
		this.clearApiFieldErrors();
		this.formError = null;
		if (!this.validateCurrentStep()) {
			this.formError = this.translateService.instant('g.invalid_input');
			return;
		}

		if (this.currentStep === 1) {
			this.currentStep = 2;
		} else if (this.currentStep === 2) {
			if (this.isNewCustomer) {
				this.currentStep = 3;
			} else if (this.isNewSite) {
				this.currentStep = 4;
				this.populateSiteEnterprises();
			} else {
				this.goToShareStepOrSubmit();
			}
		} else if (this.currentStep === 3) {
			this.currentStep = 4;
			this.populateSiteEnterprises();
		} else if (this.currentStep === 4) {
			this.goToShareStepOrSubmit();
		}
	}

	prevStep() {
		if (this.currentStep === 2) {
			this.currentStep = 1;
		} else if (this.currentStep === 3) {
			this.currentStep = 2;
		} else if (this.currentStep === 4) {
			if (this.isNewCustomer) {
				this.currentStep = 3;
			} else {
				this.currentStep = 2;
			}
		} else if (this.currentStep === 5) {
			if (this.isNewSite) {
				this.currentStep = 4;
			} else if (this.isNewCustomer) {
				// This case shouldn't be possible because new customer implies new site, but just in case
				this.currentStep = 3;
			} else {
				this.currentStep = 2;
			}
		}
	}

	goToStep(step: number) {
		if (step === 5 && !this.showShareWithCustomerStep)
			return;

		// Only allow navigating to steps that make sense
		if (step === 3 && !this.isNewCustomer)
			return;

		if (step === 4 && !this.isNewSite)
			return;

		if (step >= 1 && step <= this.totalSteps) {
			this.clearApiFieldErrors();
			this.currentStep = step;

			if (step === 4) {
				this.populateSiteEnterprises();
			}
		}
	}

	get isFinalCreateStep(): boolean {
		if (this.showShareWithCustomerStep)
			return false;

		return this.currentStep === 4 || (this.currentStep === 2 && !this.isNewCustomer && !this.isNewSite);
	}

	private goToShareStepOrSubmit(): void {
		if (this.showShareWithCustomerStep) {
			this.currentStep = 5;
			return;
		}

		this.setShareAndSubmit(false);
	}

	populateSiteEnterprises() {
		let enterpriseIds = [];
		if (this.isNewCustomer) {
			enterpriseIds = this.newCustomer.customer_enterprises || [];
		} else {
			const selectedCustomer = this.customers.find(customer => customer.id === this.studyDetailsData.customerId);
			if (selectedCustomer && selectedCustomer.customer_enterprises) {
				enterpriseIds = Array.isArray(selectedCustomer.customer_enterprises)
					? selectedCustomer.customer_enterprises
					: [selectedCustomer.customer_enterprises];
			}
		}

		this.newSite.customer_enterprises = [...enterpriseIds];
		this.customerEnterprises = this.enterprisesList.filter(e => enterpriseIds.includes(e.id));
	}

	// ====== Validation ======

	validateCurrentStep(): boolean {
		switch (this.currentStep) {
			case 1:
				this.isTruckManufacturingYearValid = true;

				const isValid = this.deviceSetupValidate?.validateForm() ?? true;
				if (this.deviceSetupData.truckManufacturingYear > moment().utc().year()) {
					this.isTruckManufacturingYearValid = false;
					this.formError = this.translateService.instant('g.invalid_input');
					return false;
				}

				return isValid; 
			case 2:
				return this.validateStudyDetailsStep();
			case 3:
				return this.newCustomerValidate?.validateForm() ?? true;
			case 4:
				return this.newSiteValidate?.validateForm() ?? true;
			case 5:
				return true;
			default:
				return true;
		}
	}

	validateStudyDetailsStep(): boolean {
		this.studyDetailsData.studyName = this.studyDetailsData.studyName?.trim() || '';
		return this.studyDetailsValidate?.validateForm() ?? true;
	}

	onBatteryTypeChange(): void {
		const availableVoltages = this.selectedBatteryVoltageOptions;
		if (!availableVoltages.includes(this.deviceSetupData.nominalBatteryVoltage))
			this.deviceSetupData.nominalBatteryVoltage = null;
	}

	get selectedBatteryVoltageOptions(): number[] {
		return this.batteryVoltageOptionsByType[this.deviceSetupData.batteryType] || [];
	}

	// ====== Map ======

	updateSiteCoords(coords) {
		this.newSite.latitude = coords.lat;
		this.newSite.longitude = coords.lng;
		this.newSite.address = coords.address;
		this.newSite.country_id = coords.country;
		this.newSite.state_id = coords.state;
		this.newSite.city = coords.city;
	}

	// ====== Step 5: Share & Submit ======

	setShareAndSubmit(share: boolean) {
		this.shareWithCustomer = share;
		this.submitStudy();
	}

	submitStudy() {
		this.formError = null;
		this.clearApiFieldErrors();

		if (!(this.deviceSetupValidate?.validateForm() ?? true)) {
			this.currentStep = 1;
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}

		this.isTruckManufacturingYearValid = true;
		if (this.deviceSetupData.truckManufacturingYear > moment().utc().year()) {
			this.isTruckManufacturingYearValid = false;
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}

		if (!this.validateStudyDetailsStep()) {
			this.currentStep = 2;
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}
		if (this.isNewCustomer && !(this.newCustomerValidate?.validateForm() ?? true)) {
			this.currentStep = 3;
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}
		if (this.isNewSite && !(this.newSiteValidate?.validateForm() ?? true)) {
			this.currentStep = 4;
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}

		// Prepare new site data
		let tempSite = null;
		if (this.isNewSite) {
			tempSite = Object.assign({}, this.newSite);
			let insDate = new Date(tempSite.installation_date);
			tempSite.installation_date = Math.floor((-insDate.getTimezoneOffset() * 60000 + insDate.getTime()) / 1000);
		}

		const formData = {
			name: this.studyDetailsData.studyName,
			customer_id: this.studyDetailsData.customerId || 0,
			site_id: this.studyDetailsData.siteId || 0,
			mac_address: this.studyDetailsData.serialNumber,
			studyId: this.studyInfo?.id || 0,

			// Device setup fields
			truck_id: this.deviceSetupData.truckId,
			truck_sn: this.deviceSetupData.truckSn,
			truck_manufacturer: this.deviceSetupData.truckManufacturer,
			truck_manufacturing_year: this.deviceSetupData.truckManufacturingYear,
			truck_type: this.deviceSetupData.truckType,
			number_of_cables: this.deviceSetupData.numberOfCables,
			battery_capacity: this.deviceSetupData.batteryCapacity,
			battery_type: this.deviceSetupData.batteryType,
			nominal_battery_voltage: this.deviceSetupData.nominalBatteryVoltage,

			// New Customer / Site Flags and Data
			is_new_customer: this.isNewCustomer,
			is_new_site: this.isNewSite,
			new_customer_data: this.isNewCustomer ? this.newCustomer : null,
			new_site_data: this.isNewSite ? tempSite : null,

			// Share flag
			share_with_customer: this.shareWithCustomer,
		};

		this.formError = null;
		this.studiesService.requestPowerStudy(formData).subscribe((res: any) => {
			if (res.api_status > 1) {
				switch (res.api_status) {
					case this.resultCode.not_found:
						this.currentStep = 2;
						this.setApiFieldError('mac_address', this.translateService.instant('rejection_log.device_not_found'));
						break;
					case this.resultCode.study_already_exists:
						this.currentStep = 2;
						this.setApiFieldError('name', this.translateService.instant('studies.study_already_exists'));
						break;
					case this.resultCode.has_queued_study:
						this.formError = this.translateService.instant('studies.has_queued_study');
						break;
					case this.resultCode.not_iotah_device:
						this.formError = this.translateService.instant('studies.not_iotah_device');
						break;
					case this.resultCode.site_not_found:
						this.formError = this.translateService.instant('site.site_not_found');
						break;
					case this.resultCode.customer_name_exists:
						this.currentStep = 3;
						this.setApiFieldError('customer_name', this.translateService.instant('customer.customer_name_already_exist'));
						break;
					case this.resultCode.site_name_exists:
						this.currentStep = 4;
						this.setApiFieldError('name', this.translateService.instant('site.site_name_already_exist'));
						break;
					case this.resultCode.incorrect_site:
						this.formError = this.translateService.instant('site.site_not_found');
						break;
				}

				this.scrollToTop();
				return;
			}
			this.successMessage = this.translateService.instant('studies.operation_done_successfully');
		});
	}

	initEditMode() {
		if (!this.editStudyData)
			return;

		this.deviceSetupData = {
			truckId: this.editStudyData.truck_id || '',
			truckSn: this.editStudyData.truck_sn || '',
			truckManufacturer: this.editStudyData.truck_manufacturer || '',
			truckManufacturingYear: this.editStudyData.truck_manufacturing_year || null,
			truckType: this.editStudyData.truck_type || '',
			numberOfCables: this.editStudyData.number_of_cables || null,
			batteryCapacity: this.editStudyData.battery_capacity || null,
			batteryType: this.editStudyData.battery_type ?? '',
			nominalBatteryVoltage: this.editStudyData.nominal_battery_voltage || null,
		};
	}

	submitEdit() {
		this.formError = null;
		this.isTruckManufacturingYearValid = true;

		if (!(this.deviceSetupValidate?.validateForm() ?? true)) {
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}

		if (this.deviceSetupData.truckManufacturingYear > moment().utc().year()) {
			this.isTruckManufacturingYearValid = false;
			this.formError = this.translateService.instant('g.invalid_input');
			this.scrollToTop();
			return;
		}

		const body = {
			study_id: this.editStudyData.id,
			truck_id: this.deviceSetupData.truckId,
			truck_sn: this.deviceSetupData.truckSn,
			truck_manufacturer: this.deviceSetupData.truckManufacturer,
			truck_manufacturing_year: this.deviceSetupData.truckManufacturingYear,
			truck_type: this.deviceSetupData.truckType,
			number_of_cables: this.deviceSetupData.numberOfCables,
			battery_capacity: this.deviceSetupData.batteryCapacity,
			battery_type: this.deviceSetupData.batteryType,
			nominal_battery_voltage: this.deviceSetupData.nominalBatteryVoltage,
		};

		this.studiesService.editStudy(body).subscribe((res: any) => {
			if (res.api_status > 1) {
				switch (res.api_status) {
					case 2:
						this.formError = this.translateService.instant('studies.study_not_found');
						break;
					case 3:
						this.formError = this.translateService.instant('studies.cannot_edit_completed_study');
						break;
					default:
						this.formError = this.translateService.instant('g.failed');
						break;
				}

				this.scrollToTop();
				return;
			}

			this.successMessage = this.translateService.instant('studies.operation_done_successfully');
		});
	}

	closeSuccess() {
		this.newStudyForm.hide();
		this.resetAll();
		this.onClose.emit(1);
	}

	cancelWizard() {
		this.onClose.emit(0);
		this.newStudyForm.hide();
		this.resetAll();
	}

	resetAll() {
		this.currentStep = 1;
		this.isNewCustomer = false;
		this.isNewSite = false;
		this.successMessage = '';
		this.formError = null;
		this.clearApiFieldErrors();
		this.shareWithCustomer = false;

		this.deviceSetupData = {
			truckId: '',
			truckSn: '',
			truckManufacturer: '',
			truckManufacturingYear: null,
			truckType: '',
			numberOfCables: null,
			batteryCapacity: null,
			batteryType: '',
			nominalBatteryVoltage: null,
		};

		this.studyDetailsData = {
			studyName: '',
			customerId: '',
			siteId: '',
			serialNumber: ''
		};

		const currentUser = this.usersService.getCurrentUser();
		this.newCustomer = { id: 0, customer_name: '', customer_contact_name: '', customer_phone_num: '', customer_enterprises: currentUser?.enterprise_id ? [Number(currentUser.enterprise_id)] : null };
		this.newSite = {
			id: 0,
			name: '',
			zone_id: null,
			installation_date: new Date(new Date().setHours(0, 0, 0, 0)),
			is_smart_rebates: false,
			password_mode: 'no_touch',
			is_verified: true,
			is_contain_iotemeter_devices: false
		};
		this.newSiteMap = { latitude: 28.623421, longitude: -81.212219 };
	}

	hasApiFieldError(fieldName: string): boolean {
		return !!this.apiFieldErrors[fieldName];
	}

	getApiFieldError(fieldName: string): string {
		return this.apiFieldErrors[fieldName] || '';
	}

	private setApiFieldError(fieldName: string, message: string): void {
		this.apiFieldErrors[fieldName] = message;
		this.formError = message;
	}

	private clearApiFieldErrors(): void {
		this.apiFieldErrors = {};
	}

	scrollToTop() {
		setTimeout(() => {
			const modal = document.querySelector('.modal.show') as HTMLElement;
			const modalBody = modal?.querySelector('.modal-body') as HTMLElement;
			const scrollTarget = modalBody && modalBody.scrollHeight > modalBody.clientHeight ? modalBody : modal;

			scrollTarget?.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		});
	}

	ngOnDestroy() {
		this.newStudyForm.hide();
	}
}
